import dayjs from 'dayjs';

import { DocumentSource, Document as PrismaDocument, RiskLevel } from '@prisma/client';

import { JsonLocation } from '~/@types/interface';
import { Feedback, getIsPolicyFeedback } from '~/components/FeedbackList';
import { Routes, UNKNOWN_BRAND_ID } from '~/config';
import { RouterOutputs } from './api';
import { getAutoOpenAddonUrl } from './urls';

// Minimal interfaces for getSortedFeedbacks to support both public and private contexts
interface MinimalFeedbackLocation {
  pageNumber: number;
  position: number;
  videoStartTime?: number;
  coordinates?: any;
}

interface MinimalFeedback {
  id: string;
  text?: string | null;
  location?: MinimalFeedbackLocation | JsonLocation | any;
  riskLevel?: RiskLevel | null;
  isNew?: boolean;
  displayText?: string;
}

interface MinimalPolicyFeedback extends MinimalFeedback {
  policy: {
    id?: string;
    name: string;
    mergeFeedback?: boolean;
  };
  policyId: string;
}

interface FeedbackDocument {
  claim?: MinimalFeedback[];
  trademarkInstance?: MinimalFeedback[];
  policyFeedback?: MinimalPolicyFeedback[];
}

export const getDocumentTitle = (
  doc: Pick<PrismaDocument, 'title' | 'fileType' | 'link' | 'publishedAt' | 'createdAt'>,
) => {
  return doc.title
    ? doc.title
    : doc.fileType === DocumentSource.SOCIAL_MEDIA
      ? `Post on ${dayjs(doc.publishedAt ?? doc.createdAt).format('MMM DD, YYYY')}`
      : doc.link;
};

type Document = RouterOutputs['scanner']['getAllDocuments']['documents'][0];

export const getLinkToDoc = (
  doc: Pick<
    Document,
    'ScannerRunDocuments' | 'task' | 'link' | 'id' | 'taskId' | 'externalId' | 'fileType'
  >,
  workspaceId?: string,
) => {
  const prefix = workspaceId ? `/${workspaceId}` : '';

  if (doc.ScannerRunDocuments.length > 0) {
    if (doc.ScannerRunDocuments[0]?.ScannerRun.scannerJob.scannerSocialMedia[0]?.id) {
      return `${prefix}${Routes.INFLUENCER_MONITORING}/scanned-page/${doc.id}`;
    } else {
      const reportId = doc.ScannerRunDocuments[0]?.scannerReportId;
      return `${prefix}${Routes.CONTENT_SCANNER}/brand-reports/${doc.ScannerRunDocuments[0]?.ScannerRun.scannerJob.brandId || UNKNOWN_BRAND_ID}/report/${reportId}/scanned-page/${doc.id}`;
    }
  } else if (doc.task?.remoteTaskManager?.id) {
    return `${prefix}${Routes.PROJECTS}/${doc.task.remoteTaskManager.id}/tasks/${doc.taskId}/files/${doc.id}`;
  } else if (doc.link) {
    return doc.link;
  } else if (doc.externalId) {
    switch (doc.fileType) {
      case DocumentSource.GOOGLE_DOC: {
        return getAutoOpenAddonUrl(`https://docs.google.com/document/d/${doc.externalId}`);
      }
      case DocumentSource.GOOGLE_SHEET: {
        return getAutoOpenAddonUrl(`https://docs.google.com/spreadsheets/d/${doc.externalId}`);
      }
      case DocumentSource.GOOGLE_SLIDE: {
        return getAutoOpenAddonUrl(`https://docs.google.com/presentation/d/${doc.externalId}`);
      }
      case DocumentSource.FIGMA: {
        return getAutoOpenAddonUrl(`https://www.figma.com/design/${doc.externalId}`);
      }
      default: {
        return '';
      }
    }
  } else {
    // TODO: Add support for other document sources
    return '';
  }
};

type Doc = RouterOutputs['task']['getDocument'];

export const getSortedFeedbacks = (
  doc: FeedbackDocument | Pick<Doc, 'claim' | 'trademarkInstance' | 'policyFeedback'> | undefined,
) => {
  const allFeedbacks = [
    ...(doc?.claim ?? []),
    ...(doc?.trademarkInstance ?? []),
    ...(doc?.policyFeedback ?? []),
  ].sort((a, b) => {
    const aLocation = a.location as JsonLocation;
    const bLocation = b.location as JsonLocation;
    return aLocation?.pageNumber! - bLocation?.pageNumber! !== 0
      ? aLocation?.pageNumber! - bLocation?.pageNumber!
      : aLocation?.position! - bLocation?.position!;
  }) as (Feedback & { displayText?: string })[];

  // check if there are any feedbacks that require merging
  let i = 0;
  while (i < allFeedbacks.length) {
    const feedback = allFeedbacks[i]!;
    if (getIsPolicyFeedback(feedback)) {
      if (feedback.policy.mergeFeedback) {
        let displayText = feedback.text!;
        // loop through all feedbacks after this to see
        // if there are any that need to be merged
        let j = i + 1;
        while (j < allFeedbacks.length) {
          const nextFeedback = allFeedbacks[j]!;
          if (getIsPolicyFeedback(nextFeedback) && nextFeedback.policy.id === feedback.policy.id) {
            displayText += `${nextFeedback.text}, `;
            allFeedbacks.splice(j, 1);
          } else {
            j++;
          }
        }
        allFeedbacks[i]!.displayText = displayText;
      }
    }
    i++;
  }

  return allFeedbacks;
};

export const getPlatform = (link: string) => {
  return link.includes('instagram.com')
    ? 'Instagram'
    : link.includes('tiktok.com')
      ? 'TikTok'
      : link.includes('x.com')
        ? 'X'
        : link.includes('linkedin.com')
          ? 'LinkedIn'
          : link.includes('facebook.com')
            ? 'Facebook'
            : link.includes('apple.com')
              ? 'Apple App Store'
              : link.includes('play.google.com')
                ? 'Android Play Store'
                : link.includes('youtube.com')
                  ? 'Youtube'
                  : 'Website';
};

export type Platform = ReturnType<typeof getPlatform>;
